import { createFileRoute } from '@tanstack/react-router';
import { Suspense, useCallback, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { VStack } from '@/components/Layout';
import { ViewportLoader } from '@/components/Loading/ViewportLoader';

import {
  DocumentTabs,
  PageHeader,
  PdfViewer,
  RejectCompleteView,
  RejectDialog,
  SignatureCompleteView,
  SignatureConfirmationView,
  SignatureFooter,
  SignatureInputDialog,
} from './-components';
import {
  useDownloadSignatureResultFiles,
  useRejectSignatureRequestMutation,
  useSignatureRequestDetailQuery,
  useSignatureRequestFilePdfUrl,
  useSignPdfDocumentMutation,
} from './-query';
import {
  SignatureFlowStore,
  useSignatureFlowStore,
} from './-store/signatureFlowStore';

export const Route = createFileRoute(
  '/_individual/individual/contract/$id/signature-request/$signatureRequestId/',
)({
  component: () => (
    <Suspense fallback={<ViewportLoader />}>
      <SignatureRequestPageProvider />
    </Suspense>
  ),
});

/**
 * ストアProviderでラップするコンポーネント
 */
function SignatureRequestPageProvider() {
  const store = SignatureFlowStore.createStore();

  return (
    <SignatureFlowStore.Provider value={store}>
      <SignatureRequestPage />
    </SignatureFlowStore.Provider>
  );
}

/**
 * 署名依頼ページのメインコンポーネント
 */
function SignatureRequestPage() {
  const { signatureRequestId } = Route.useParams();
  const { data } = useSignatureRequestDetailQuery(signatureRequestId);

  // ストアから状態とアクションを取得
  const {
    currentStep,
    isDialogOpen,
    isRejectDialogOpen,
    signatureName,
    isSubmitting,
    isDownloading,
    selectedFileId,
    actions,
  } = useSignatureFlowStore(
    useShallow((s) => ({
      currentStep: s.currentStep,
      isDialogOpen: s.isDialogOpen,
      isRejectDialogOpen: s.isRejectDialogOpen,
      signatureName: s.signatureName,
      isSubmitting: s.isSubmitting,
      isDownloading: s.isDownloading,
      selectedFileId: s.selectedFileId,
      actions: s.actions,
    })),
  );

  // 初回マウント時に最初のファイルを選択
  useEffect(() => {
    actions.initializeSelectedFileId(data.files[0]?.fileId ?? null);
  }, [actions, data.files]);

  // 署名完了済みまたは却下済みの場合は該当画面に遷移
  useEffect(() => {
    if (data.status === 'COMPLETED') {
      actions.initializeStep('complete');
    } else if (data.status === 'REJECTED') {
      actions.initializeStep('rejected');
    }
  }, [actions, data.status]);

  // 電子署名実行mutation
  const signPdfDocumentMutation =
    useSignPdfDocumentMutation(signatureRequestId);

  // 署名依頼却下mutation
  const rejectSignatureRequestMutation =
    useRejectSignatureRequestMutation(signatureRequestId);

  // 署名確定ハンドラ
  const handleConfirmSignature = useCallback(async () => {
    await actions.confirmSignature(async () => {
      await signPdfDocumentMutation.mutateAsync({
        signerName: signatureName,
      });
    });
  }, [actions, signPdfDocumentMutation, signatureName]);

  // 却下ハンドラ
  const handleReject = useCallback(
    async (reason: string) => {
      await actions.rejectSignature(async () => {
        await rejectSignatureRequestMutation.mutateAsync({
          rejectionReason: reason,
        });
      });
    },
    [actions, rejectSignatureRequestMutation],
  );

  // 署名結果ファイルダウンロード
  const { download: downloadSignatureResultFiles } =
    useDownloadSignatureResultFiles(signatureRequestId);

  // ダウンロードハンドラ
  const handleDownload = useCallback(async () => {
    await actions.download(downloadSignatureResultFiles);
  }, [actions, downloadSignatureResultFiles]);

  // 選択中のファイルのPDFをダウンロードしてObject URLを取得
  const { pdfObjectUrl, isLoading: isPdfLoading } =
    useSignatureRequestFilePdfUrl(signatureRequestId, selectedFileId);

  // 最終確認画面を表示
  if (currentStep === 'confirmation') {
    return (
      <SignatureConfirmationView
        signatureName={signatureName}
        files={data.files}
        onBack={actions.backToReview}
        onConfirm={handleConfirmSignature}
        isSubmitting={isSubmitting}
      />
    );
  }

  // 完了画面を表示
  if (currentStep === 'complete') {
    return (
      <SignatureCompleteView
        onDownload={handleDownload}
        isDownloading={isDownloading}
      />
    );
  }

  // 却下完了画面を表示
  if (currentStep === 'rejected') {
    return <RejectCompleteView />;
  }

  // PDF確認画面を表示
  return (
    <>
      <VStack className="min-h-screen bg-neutral-gray-light-7">
        {/* ページヘッダー */}
        <PageHeader />

        {/* メインコンテンツ */}
        <div className="relative flex flex-1">
          {/* 左サイドバー - 文書タブ */}
          <div className="absolute left-6 top-6 z-10 w-[200px]">
            <DocumentTabs
              files={data.files}
              selectedFileId={selectedFileId}
              onSelectFile={actions.selectFile}
            />
          </div>

          {/* PDFビューワー */}
          <div className="flex-1 px-12 py-7">
            <div className="mx-auto max-w-dialog-xl h-full">
              <PdfViewer pdfUrl={pdfObjectUrl} isLoading={isPdfLoading} />
            </div>
          </div>
        </div>

        {/* フッター */}
        <SignatureFooter
          onNext={actions.openDialog}
          onReject={actions.openRejectDialog}
        />
      </VStack>

      {/* 署名入力ダイアログ */}
      <SignatureInputDialog
        open={isDialogOpen}
        onOpenChange={actions.setDialogOpen}
        onNext={actions.submitSignatureName}
        defaultSignatureName={signatureName}
      />

      {/* 却下理由入力ダイアログ */}
      <RejectDialog
        open={isRejectDialogOpen}
        onOpenChange={actions.setRejectDialogOpen}
        onReject={handleReject}
        isSubmitting={isSubmitting}
      />
    </>
  );
}
